import { isNil, omitBy } from 'lodash';
import type { AssetThumbScrubFragment } from '../gql/generated/AssetThumbScrub';

// Extract only the types from the union that have a media property
type AssetWithMedia = Extract<AssetThumbScrubFragment, { media?: any }>;

/**
 * Type guard to check if an asset has a media property
 */
function hasMedia(asset: AssetThumbScrubFragment): asset is AssetWithMedia {
  return 'media' in asset;
}

/**
 * The getThumbScrub function puts together the data needed for the thumb scrubs
 * based on the results of the AssetThumbScrub fragment. This needs to be
 * merged with the data pulled for getThumb to get all the fields needed to
 * render a VideoAsset thumbnail using MediaThumbnail.
 *
 * @param data The AssetThumbScrubFragment instance (expects a VideoAsset)
 * @returns media The partially constructed media object for MediaThumbnail
 */
export function getThumbScrub(asset: AssetThumbScrubFragment) {
  // Start with defaults
  const result = {
    metadata: {
      originalHeight: 0,
      originalWidth: 0,
    },
    thumbs: {
      thumbScrub: '',
      thumbScrubFrameCount: 50,
      thumbScrubTileX: 1,
      thumbScrubTileY: 50,
      thumbScrubOriginalHeight: 0,
      thumbScrubOriginalWidth: 0,
    },
  };

  // If no media, return defaults
  if (!hasMedia(asset)) {
    return result;
  }

  const metadata = asset.media?.metadata;
  const thumbsScrubSprite = asset.media?.thumbScrubSprite;

  if (
    metadata &&
    thumbsScrubSprite &&
    thumbsScrubSprite.frames &&
    thumbsScrubSprite.frames > 1
  ) {
    const thumbs = {
      thumbScrub: thumbsScrubSprite.streamUrl,
      thumbScrubFrameCount: thumbsScrubSprite.frames,
      thumbScrubTileX: thumbsScrubSprite.tileX,
      thumbScrubTileY: thumbsScrubSprite.tileY,
      thumbScrubOriginalHeight: metadata.originalHeight,
      thumbScrubOriginalWidth: metadata.originalWidth,
    };
    return {
      metadata: Object.assign(result.metadata, omitBy(metadata, isNil)),
      thumbs: Object.assign(result.thumbs, omitBy(thumbs, isNil)),
    };
  }

  return result;
}
